const faqData = [
  {
    category: "Getting Started",
    questions: [
      {
        q: "What is Axaris?",
        a: "Axaris is an enterprise cybersecurity risk assessment platform. It runs guided assessments across your organization (Enterprise), M&A deals, business units, third-party vendors (TPRM), ransomware readiness, dark web exposure, and AI governance, and connects your security tools through the GRC / Security Insights module. Everything Axaris finds lands in a single risk register with remediation tracking and financial exposure estimates."
      },
      {
        q: "How do I sign in to Axaris?",
        a: "Click the sign-in button on the sign-in page. On the hosted Axaris service it is 'Sign in with Microsoft', and authentication is handled entirely by your work or school Microsoft account. If your organization hosts Axaris itself, the button names your organization's sign-in instead; if you have no sign-in there yet, choose 'Register' on the page that opens, if your installation offers it, and use the email address your invitation was sent to. 'Forgot password?' on that same page resets your password. Either way, Axaris never sees or stores your password. After signing in, pick the organization you want to work in."
      },
      {
        q: "How do I start my first assessment?",
        a: "Open the module you want from the navigation bar (for example Enterprise, TPRM, or Ransomware) and go to its assessments page. An assessment must be assigned to a user before it can be filled in: click Assign to pick the person responsible, then the assignee clicks Assess to answer the questionnaire."
      },
      {
        q: "Why did I land on My Tasks instead of the Dashboard?",
        a: "If your only role in the organization is scoped to your own assignments (for example Contributor, Approver, or a read-only role at self scope), Axaris takes you straight to My Tasks and shows a reduced menu. Everything assigned to you — assessments, risks, and approvals — is collected there."
      },
      {
        q: "Why don't I see a module (e.g. Ransomware or TPRM) in my menu?",
        a: "Modules appear only if your organization is licensed for them and your role permits them — unlicensed modules are hidden entirely. Check with your organization admin first, then Axaris support about licensing."
      },
      {
        q: "Why is one of my organizations locked with 'IP Restricted'?",
        a: "That organization limits access to an approved list of network addresses, and yours is not on it. The tile shows your detected IP address — ask your administrator to add it to the organization's Allowed IP Ranges."
      }
    ]
  },
  {
    category: "Assessments",
    questions: [
      {
        q: "How do assessments work?",
        a: "Assessments are structured questionnaires tailored to a specific risk domain. You answer questions about your security practices, policies, and controls, optionally attaching supporting documents. When you submit, Axaris analyzes your responses with AI, calculates risk scores, and generates specific risks with remediation guidance."
      },
      {
        q: "What types of assessments are available?",
        a: "Axaris has seven questionnaire-based assessment flows: deal application assessments (in the Deals module), Enterprise, Business Unit, TPRM (per vendor), Ransomware, Dark Web, and AI governance. The GRC / Security Insights module works differently — it derives risks from your connected security tools rather than a questionnaire."
      },
      {
        q: "What answer options do assessment questions have?",
        a: "Every question is answered Yes, Partially, No, or Not Applicable. You can attach supporting documents per question (PDF, Word, Excel, images, or text, up to 10 MB per file) with a document type such as Network Diagram, Security Policy, Pen Test Report, or Certification."
      },
      {
        q: "Can I pause an assessment and finish it later?",
        a: "Yes. Answers are saved automatically as you move between questions, and reopening the assessment via Assess resumes where you left off — along with your notes, flags, and previously uploaded attachments."
      },
      {
        q: "How are assessment questions organized?",
        a: "Questions are grouped into categories, and a Sections panel down the left-hand side lists each category with how many of its questions you have answered (a green tick marks a completed section). Click any section to jump straight to its first question — useful for working through one domain at a time or splitting a long questionnaire between colleagues."
      },
      {
        q: "Can I add notes to a question or flag it for follow-up?",
        a: "Yes, on every assessment flow. Use the Notes field under a question to record context or where the evidence came from, and the flag icon to mark a question you need to come back to. Both are saved with the assessment, and you can flag or annotate a question without answering it first."
      },
      {
        q: "How do I find the questions I still need to deal with before submitting?",
        a: "On the Review Your Answers page, use the filter to show only Unanswered, Flagged only, or Has notes questions, and the sort to put Flagged first. That is the quickest way to clear outstanding queries before you submit."
      },
      {
        q: "Why did extra questions appear after I answered one?",
        a: "Vendor (TPRM) questionnaires include follow-up questions that only show when a previous answer makes them relevant — answer that you process personal data, for example, and the data-protection questions appear beneath it. Change the parent answer and they disappear again. Hidden follow-ups are not counted against you in the section totals or the analysis."
      },
      {
        q: "Why is the Assess button disabled for me?",
        a: "An assessment must first be assigned, and only the assigned user can fill it in. The button is also disabled while the assessment is analyzing or once it is complete. Ask a Risk Manager (or a deal/business-unit manager for that scope) to assign or reassign it."
      },
      {
        q: "Can I assign assessments to team members?",
        a: "Yes. Risk Managers and above (including managers scoped to a specific deal or business unit) click Assign on the assessment to pick an owner. The assignment appears in that person's My Tasks. In TPRM you can also invite an external vendor contact by email."
      },
      {
        q: "How does AI-powered analysis work in assessments?",
        a: "When you submit an assessment, Axaris analyzes your answers and any supporting documents with AI. It identifies gaps in your security posture, generates specific risks with severity ratings and step-by-step remediation guidance, and estimates the financial exposure of each finding. AI outputs should always be independently validated."
      },
      {
        q: "How long does analysis take after I submit?",
        a: "Typically a few minutes, sometimes 10 minutes or longer. You can safely leave the analyzing screen — results appear in the risks view when analysis completes. If analysis fails, use Retry Analysis or contact support."
      },
      {
        q: "What do the assessment status badges mean?",
        a: "In Progress means the questionnaire is being answered; Assessing means AI analysis is running; Complete means results are ready; Assessment Failed means analysis did not finish and can be retried. Newly assigned, unanswered assessments show no badge."
      },
      {
        q: "Can I assess with documents instead of a questionnaire?",
        a: "Yes, in the Enterprise and TPRM modules. Use Assess Document to upload a single PDF, DOCX, TXT, or CSV up to 10 MB (for example a SOC 2 report). The file is malware-scanned, then AI derives findings from it. If earlier results exist, Axaris asks whether to start fresh, replace only document findings, or keep everything and add new findings."
      },
      {
        q: "What does Reassess do, and who can delete an assessment?",
        a: "Reassess (Risk Manager and above, completed assessments only) deletes the assessment's risks and remediations and reruns analysis — your questionnaire answers are preserved, and you must type 'Confirm' to proceed. Deleting an assessment entirely requires an organization Admin."
      }
    ]
  },
  {
    category: "Risk Scores & Severity",
    questions: [
      {
        q: "How are risk scores calculated?",
        a: "Risk scores are calculated from your assessment responses, weighted by the criticality of each security domain. Each generated risk also carries its own severity score, and module dashboards roll these up into module-level scores and gauges."
      },
      {
        q: "What do the severity levels mean?",
        a: "Axaris uses four severity levels: Critical (immediate action required — significant vulnerabilities that could lead to major breaches), High (urgent attention needed — notable gaps in security controls), Medium (should be addressed in near-term planning), and Low (minor issues for long-term improvement)."
      },
      {
        q: "How does the Dashboard's Overall Score work?",
        a: "The Overall Score is a weighted 0-100% measure of your current active risk across licensed modules: under 25% is Low, 25-49% Medium, 50-74% High, and 75%+ Critical. It is a current-state number; the Risk & Remediation Trend chart next to it tracks risk and remediation counts over time rather than the score itself."
      },
      {
        q: "What is the Predictive Risk Timeline?",
        a: "A projection of your risk score at 45, 90, and 180 days if no action is taken, weighted by each module's share of your active risks. It shows the cost of inaction, not a guaranteed outcome."
      },
      {
        q: "What are External Risks on the Dashboard?",
        a: "Findings sourced from integrations rather than questionnaires — surface scans, secrets scanning, dark web exposures, and live threat feeds. The External Risks card opens the Risks page filtered to those sources."
      }
    ]
  },
  {
    category: "Assessment Modules",
    questions: [
      {
        q: "What does the Enterprise module assess?",
        a: "The Enterprise module evaluates your organization's overall cybersecurity posture — governance, access controls, data protection, incident response, network security, and compliance. You can run it organization-wide, per deal, or per business unit, and it also supports document-only assessments."
      },
      {
        q: "What does the TPRM module assess?",
        a: "The Third-Party Risk Management module evaluates risks associated with your vendors. It combines vendor questionnaires (or uploaded documents like SOC 2 reports) with vendor classification, financial exposure modeling, framework readiness, deterioration forecasts, and external threat intelligence on each vendor."
      },
      {
        q: "What does the Ransomware module assess?",
        a: "The Ransomware module evaluates your preparedness against ransomware attacks — backups, endpoint protection, email security, awareness training, incident response, and recovery. It adds live threat intelligence scanning, a scenario simulator, kill-chain exposure paths, and executive briefs."
      },
      {
        q: "What does the Dark Web module assess?",
        a: "The Dark Web module combines a questionnaire assessment of your exposure-management practices with continuous monitoring: compromised-credential detection for your domains, dark-web channel signals and threat incidents, external attack surface scans, and secrets scanning of code repositories."
      },
      {
        q: "What does the AI module assess?",
        a: "The AI module evaluates your organization's governance and risk management around artificial intelligence — AI policy, data privacy, model security, ethics, bias mitigation, and regulatory compliance. It is about your AI usage; it is separate from the AI analysis Axaris itself runs on assessments."
      },
      {
        q: "What does the GRC / Security Insights module do?",
        a: "It connects your security tools (Microsoft 365/Azure, Okta, AWS, Google Cloud, Tenable, Wiz, Splunk, KnowBe4, Fortinet, Azure DevOps, Snowflake) and continuously evaluates security controls from their data. It scores you against 17 compliance frameworks, automatically raises risks for control gaps, closes them when controls recover, and analyzes your policy documents."
      }
    ]
  },
  {
    category: "Deals & Business Units",
    questions: [
      {
        q: "What are Deals in Axaris?",
        a: "Deals represent M&A transactions or business engagements that require cybersecurity due diligence. Each deal contains applications, and each application gets its own assessment, letting you evaluate the security posture of target companies or partners."
      },
      {
        q: "What are Business Units?",
        a: "Business Units represent departments, divisions, or subsidiaries within your organization. They let you scope assessments to specific parts of the business. The Self checkbox when creating one auto-fills it from your organization's details, so you can assess your whole organization as a single unit."
      },
      {
        q: "How do Deals and Business Units relate to assessments?",
        a: "Most modules (Enterprise, Ransomware, Dark Web, AI, TPRM) can run assessments at organization, deal, or business-unit level. This lets you run the same assessment type across different contexts and compare results to prioritize where mitigation matters most."
      },
      {
        q: "How do I create a new Deal or Business Unit?",
        a: "Go to Deals or Business Units in the navigation and click New Deal or the equivalent create button (requires Risk Manager or higher). Fill in the name, Data Residency Region, and other details, then save. You can then add applications and start assessments within it."
      },
      {
        q: "What is the Data Residency Region?",
        a: "It controls where a deal's, business unit's, application's, or vendor's data is stored. It defaults to your organization's home region (or the parent deal or business unit's region for applications) but can be changed when the item is created. It cannot be changed afterwards."
      },
      {
        q: "What is a private deal?",
        a: "A private deal is visible only to the people given a role on that specific deal — organization-wide Risk Managers, Admins, and Owners included. Its applications, assessments, risks, remediations, and vendors are hidden along with it, everywhere: lists, dashboards, reports, and search. Tick Private deal when you create the deal, or use Make private on the deal afterwards. It is the one place where a broader organization role does not grant access."
      },
      {
        q: "Who can make a deal private, and who can share it?",
        a: "Owner access on the deal, or being the person who created it, lets you switch privacy on and off. Admin or Owner access on the deal lets you use Manage access to add and remove people. When you make a deal private, anyone with work already assigned on it is automatically given Contributor access so their assigned work does not disappear — review them under Manage access afterwards."
      },
      {
        q: "On a deal, why does Contributor see less than Viewer?",
        a: "At deal scope the level decides how much of the deal you can see, not how senior you are. Contributor and Approver see only the work assigned to them; Viewer reads the whole deal. Pick the level by what the person should be able to see."
      },
      {
        q: "What is the Executive Summary report?",
        a: "On a deal application's or business unit's Assessment Risks & Controls page, the Executive Summary button generates an AI-written PDF briefing: posture at a glance, a written summary of what the assessment found, the top ten prioritized remediation recommendations, and the top ten open risks. Completed remediations are excluded, so the figures reflect what is still outstanding."
      }
    ]
  },
  {
    category: "Risk Management",
    questions: [
      {
        q: "What is the Risk Register?",
        a: "The Risk Register holds risks your organization has formally accepted, with the acceptance reason, approver decisions, and a full activity history. Active risks live on the Risks page; accepting a risk (with approval) moves it to the register, and Reopen moves it back."
      },
      {
        q: "Can I accept or mitigate risks?",
        a: "Yes. For each risk you can mitigate it (work its remediation, update status and evidence via Manage) or accept it (acknowledge it with a documented justification). Acceptance always goes through an approval workflow."
      },
      {
        q: "How does the risk approval workflow work?",
        a: "When you click Accept Risk, you pick an approver and give a required reason. The risk enters a waiting-on-approval state and the approver is emailed. The approver can approve or reject, and can optionally add a secondary approver whose sign-off is also required. Approval moves the risk to the Risk Register; rejection returns it to the active list."
      },
      {
        q: "Who can edit a risk?",
        a: "Access is additive — any one of these paths is enough: a Risk Manager (or higher) role for the whole organization, a Risk Manager role on the risk's deal, one on its business unit, or being directly assigned to the risk. Read-only users see no action buttons."
      },
      {
        q: "Can an accepted risk be reversed?",
        a: "Yes. On the Risk Register, Reopen returns the risk to the active Risks page and clears its acceptance and approval fields. The activity log keeps the full history."
      },
      {
        q: "Can a risk acceptance be given an expiry date?",
        a: "Yes, and it is worth doing — an acceptance is a decision made against the circumstances of the day. Set an Acceptance expiry date when you request the acceptance. Axaris emails the requester and the approver once as the date approaches (falling back to your organization's risk managers if neither can be reached), and after it passes the risk shows an 'Acceptance expired' badge in the risk list and Risk Register."
      },
      {
        q: "What happens when an acceptance expires?",
        a: "Nothing automatic — Axaris flags it for a human decision rather than making one for you. The risk keeps its accepted status but is badged 'Acceptance expired'. Open it in the Risk Register and click Reopen to bring it back to the active list, then either remediate it or request a fresh acceptance with a new reason, approver, and expiry date."
      },
      {
        q: "Is a reason required when updating a risk?",
        a: "Yes — the Manage Risk dialog requires a Reason for Change, which is written to the risk's activity log. Evidence attachments are optional (up to 10 MB; PDF, Word, Excel, image, or text)."
      }
    ]
  },
  {
    category: "Remediation",
    questions: [
      {
        q: "How does the remediation workflow work?",
        a: "Every risk comes with a remediation tracked on the Remediation Tracker. Each has an owner, priority, due date, and status: Not Started, Planned, In Progress, Under Review, or Completed, plus Deferred for parked work. Completed and Deferred items leave the default pending view and never show as overdue."
      },
      {
        q: "Does Axaris remind assignees about due dates?",
        a: "Yes. The remediation owner gets automatic email reminders 45, 30, and 15 days before the target date, then every 7 days once the remediation is overdue."
      },
      {
        q: "Does Axaris integrate with Jira and ServiceNow?",
        a: "Yes. Once an integration is configured under Settings > Integrations, you can create a Jira issue or ServiceNow incident directly from a remediation, with priority mapped automatically. Axaris checks linked tickets in the background: when the Jira issue is done or the ServiceNow incident is resolved or closed, the remediation is automatically marked completed."
      }
    ]
  },
  {
    category: "Financial Exposure",
    questions: [
      {
        q: "What do the financial figures on risks mean?",
        a: "Each risk carries a FAIR-based Expected Annual Loss (EAL) range — the estimated annual cost of the risk under conservative-to-elevated assumptions, with a Most Likely value Axaris uses for sorting and aggregation. The Financial Impact tab breaks this into direct costs, business interruption, recovery, regulatory fines, and reputation damage."
      },
      {
        q: "Why do Monte Carlo numbers look bigger than the Expected Annual Loss?",
        a: "The EAL is the average simulated year, while the upper Monte Carlo percentiles (P90-P99) describe progressively worse years, so they naturally read higher. Use the bands for insurance and reserve planning, and the annualized EAL for yearly budgeting."
      },
      {
        q: "Why does a risk show 'financial analysis not yet available'?",
        a: "Financial enrichment runs in the background after a risk is created. Figures appear once analysis completes, typically within minutes; assessments show a calculating indicator in the meantime."
      }
    ]
  },
  {
    category: "Users & Permissions",
    questions: [
      {
        q: "What user roles are available?",
        a: "Axaris offers six assignable roles: Admin — full organization management, user administration, settings, and all features. Enterprise Risk Manager — organization-wide assessment and risk oversight across all deals and business units. Risk Manager — creates deals and business units and manages their own assessments and risks. Contributor — completes assigned assessments and updates assigned risks. Approver — reviews and approves or rejects risk acceptance requests. Auditor — read-only access to all dashboards, assessments, reports, and risk registers. You can see a quick reference of all roles in the Role Guide on the User Management page."
      },
      {
        q: "What can each role do?",
        a: "Admin: full control over the organization — manage users, configure settings, create and manage all assessments, risks, and remediation items. Enterprise Risk Manager: view and manage all assessments and risks across the organization, but not users or settings. Risk Manager: create deals and business units, manage assessments and risks they own or are assigned to. Contributor: complete assessments assigned to them, update risks they are assigned to. Approver: review and decide risk acceptance requests routed to them. Auditor: view everything in read-only mode."
      },
      {
        q: "What are the different scope levels?",
        a: "Roles can be assigned at different scope levels that determine breadth of access: Tenant — spans all organizations in the tenant. Organization — everything within one organization. Business Unit — limited to a specific business unit. Deal — limited to a specific deal. Self — limited to items the user creates or is explicitly assigned. (An Account level above Tenant also exists for cross-tenant administration.)"
      },
      {
        q: "How do role permissions change at different scopes?",
        a: "Each role's capabilities apply at the scope where it is assigned. An Admin at Tenant scope manages users and settings across the whole tenant; at Org scope, one organization; at Deal scope, that deal only. A Risk Manager at Org scope manages risks and assessments organization-wide, while at Self scope only their own. Remember access is additive — a narrow role plus a direct assignment can grant edit rights an org-level role would not."
      },
      {
        q: "How do I invite new users?",
        a: "Administrators invite users from the User Management page (Settings > Users). Click Add User, enter the person's business email address (public email domains are rejected), full name, and role, then Send Invite — or use the Invite Team tab to invite several people at once. Invitees accept the emailed link, sign in, and accept the terms. Track and revoke invitations on the Pending Invitations tab."
      },
      {
        q: "Where can I see a summary of all roles?",
        a: "The User Management page includes a Role Guide panel showing all six assignable roles with color-coded badges and short descriptions. For detailed, scope-level assignments, click Edit Roles on any user and open the advanced role editing view."
      },
      {
        q: "Can a user have different roles in different organizations?",
        a: "Yes. A user can hold a different role in each organization they belong to — for example Admin in one and Contributor in another. Permissions are evaluated for the currently selected organization; switch organizations with the Change link under the navigation bar or via the Settings menu."
      },
      {
        q: "How do I see who has viewed or downloaded a document?",
        a: "Open Settings > Document Access (Risk Manager or higher). It is the audit trail of every upload, edit, deletion, view, and download across remediation evidence, TPRM supporting documents, GRC control evidence, and vendor documents, showing when, who, what action, which document, and its context. Filter by context, action, file name, and date range."
      }
    ]
  },
  {
    category: "Organization Management",
    questions: [
      {
        q: "How do I switch between organizations or tenants?",
        a: "Use the Change link next to the organization name just below the navigation bar, or open the Settings gear menu and choose Organizations. Both take you to the selection page, where organizations are grouped by tenant."
      },
      {
        q: "What is the difference between a Tenant and an Organization?",
        a: "A Tenant is a top-level grouping (such as an MSSP or parent company) that can contain multiple Organizations. An Organization is a specific entity with its own assessments, users, and data. Users can hold different roles across organizations within the same tenant."
      },
      {
        q: "Who can create a new organization?",
        a: "Tenant admins — the + New Organization button appears on the organization selection page. Provisioning takes about one to two minutes, and the organization's Data Residency Region cannot be changed after creation."
      },
      {
        q: "What subscription plans are available?",
        a: "Axaris modules are licensed per organization, and unlicensed modules are hidden from the navigation. The Subscription Management page shows plans, add-ons, and current usage for comparison, but purchases are not processed in-app — licenses are granted by Axaris, so contact your account administrator or the Axaris team to change your entitlements."
      }
    ]
  },
  {
    category: "Dark Web Monitoring",
    questions: [
      {
        q: "How does Dark Web monitoring work?",
        a: "Axaris continuously monitors breach data and dark-web channels for your enrolled domains. Compromised-credential exposures appear in the Live Monitoring Dashboard — Realtime entries carry full breach detail, while Historical entries show a coarse detection window. Channel signals that correlate to your organization automatically become Threat Incidents."
      },
      {
        q: "How do I configure domain monitoring?",
        a: "Go to Dark Web > Live Monitoring Config, enter your domain under Primary Domain, and click Enroll. You can add Secondary Domains, or enroll deals, business units, and vendors via Add Deal, Add Business Unit, and Add Vendor."
      },
      {
        q: "What does the red badge on the Dark Web menu mean?",
        a: "It counts new live-monitoring findings since you last opened the Live Monitoring Dashboard. Opening that dashboard clears it."
      },
      {
        q: "What are Threat Incidents?",
        a: "Incidents created automatically when dark-web channel signals correlate against your organization profile — typed as Credential Leak, Ransomware Naming, IAB Listing, Phishing Infra, or CVE Exploit. Each includes a narrative, affected emails or assets, recommended actions, and an SLA due date."
      },
      {
        q: "What does Secrets Scanning cover?",
        a: "Repositories reached through a Source Code Connection (GitHub, GitLab, or Azure DevOps) set up under Settings > Integrations and referenced by each scan target, plus optional Domain Keywords searched across public GitHub to catch leaks in third-party repositories. Organization Risk Managers (or higher) can run Scan Now and mark findings Resolved, Acknowledged, or False Positive."
      },
      {
        q: "What is the Attack Surface Score?",
        a: "The Surface Scan's health score for your external attack surface. It starts at 100 and deducts points per open finding by severity: 15 for Critical, 8 for High, 3 for Medium, and 1 for Low."
      }
    ]
  },
  {
    category: "Vendors & TPRM",
    questions: [
      {
        q: "Can I send a TPRM assessment to someone at the vendor who doesn't have an Axaris account?",
        a: "Yes. In the Assign Assessment dialog, invite them by business email (public email domains are rejected). The assignment shows as Invited until they accept; on acceptance their account is created, they accept the vendor-invitee terms, and the assessment transfers to them automatically."
      },
      {
        q: "What do the vendor classification fields affect?",
        a: "Data Sensitivity, Access Scope, and Revenue Dependency drive the Vendor Importance multiplier used in financial estimates and the automatic tier (1-3). Until they are set, estimates fall back to industry averages and the vendor dashboard warns that classification is incomplete."
      },
      {
        q: "What are AVI and RPI on the vendor dashboard?",
        a: "AVI (Axaris Vendor Impact) is the simulated mean expected annual loss weighted by vendor importance. RPI (Ransomware Prediction Index) is the probability of a ransomware-related event within 90 days — derived from the FAIR loss-event frequency across the vendor's ransomware-related risks, adjusted upward for elevated current ransomware campaign conditions, and combined with live threat intelligence."
      },
      {
        q: "Who sees the full vendor portfolio?",
        a: "Users with organization-level visibility see the full TPRM Portfolio View. Users scoped to a deal or business unit see a filtered portfolio report limited to their scope."
      },
      {
        q: "Can more than one person fill in the same vendor assessment?",
        a: "Yes. Click Respondents on the assessment to add colleagues or invite external vendor contacts. They all work on one shared form and see each other's answers, and the category sections make it easy to split the questionnaire by specialism. One respondent is marked Primary — only they, or someone with management rights, can submit it. Each respondent has their own status (Pending, Accepted, Declined, Completed)."
      },
      {
        q: "What is the vendor Performance Scorecard?",
        a: "It records how well a vendor performs as a supplier — delivery, responsiveness, quality, commercial conduct — scored out of 5 by your business owner, on the Performance tab of the vendor dashboard. Alongside the vendor's cyber-risk and financial figures it tells you whether a vendor is worth the risk it carries. Reviews produce an overall score, a per-theme breakdown, a trend against the previous review, and a recommendation label from Preferred Vendor to High Concern."
      },
      {
        q: "How often are performance reviews issued?",
        a: "Automatically, on a cadence based on the vendor's tier, tightened if the vendor has an active Critical or High risk. Risk Managers can override it to every 3, 6, or 12 months on the Performance tab, or click Issue Scorecard Now to send one immediately. The reviewer — the vendor's business owner by default — is notified by email and finds the review in My Tasks."
      },
      {
        q: "Where do I store vendor contracts and certificates?",
        a: "On the Documents tab of the vendor dashboard. Each document has a type (Contract, Certification, Insurance, SOC Report, Policy, Other), a description, and a review date with a reminder lead time. Axaris emails the vendor owner as the review date approaches, and the list badges documents that are due soon or overdue."
      }
    ]
  },
  {
    category: "Security Insights (GRC)",
    questions: [
      {
        q: "Which security tools can feed Security Insights?",
        a: "Microsoft 365/Azure (Secure Score, Entra ID, Defender, Sentinel), Okta, AWS Security Hub, Google Cloud, Tenable, Wiz, Splunk, KnowBe4, Fortinet, Azure DevOps, and Snowflake — all configured under Settings > Integrations."
      },
      {
        q: "How often does Security Insights update?",
        a: "On demand — click Refresh Data (requires an organization-level Risk Manager). All connected sources sync in parallel and the refresh can take up to a couple of minutes."
      },
      {
        q: "What happens when a security control gap is found?",
        a: "Axaris automatically creates a risk for the control gap in the risk register, enriched with remediation steps and financial impact. When the control returns to Implemented, the risk closes automatically. You can also override verdicts with Mark Implemented or Mark Invalid, and regression alerts are emailed to Owners, Admins, and Risk Managers."
      },
      {
        q: "Can I upload policy documents?",
        a: "Yes — policy slots accept PDF or DOCX up to 20 MB. Documents are malware-scanned and AI-analyzed, and Axaris checks them against your actual posture: contradictions raise severity-rated conflict alerts. Replacing a policy deletes the risks and remediations generated from the old version, and Axaris warns you first."
      },
      {
        q: "Which compliance frameworks does Axaris score against?",
        a: "17 frameworks, including SOC 2, ISO 27001, NIST CSF, CIS v8, NIST 800-53, HIPAA, PCI DSS v4, SOX ITGC, FedRAMP, CMMC L2, GDPR Art. 32, Cyber Essentials, HITRUST CSF, DORA, NIST 800-171, ISO 27701, and COBIT 2019."
      }
    ]
  },
  {
    category: "Ransomware Tools",
    questions: [
      {
        q: "What does the Ransomware Scenario Simulator do?",
        a: "On the assessment risks view, you tick risks you would hypothetically close (at least three with financial data) and Axaris re-runs the financial simulation, showing expected annual loss and worst-case figures before and after, the reduction, and the cost-category breakdown. It never modifies your data."
      },
      {
        q: "What is the ransomware executive brief?",
        a: "A separate executive view (available to anyone with at least organization Viewer access) that generates a narrative of your ransomware posture for different audiences — CISO, CFO, CIO, CEO, and Board — plus anomaly and 30-day analyses. Generating narratives requires risk-management permission; results are cached per voice."
      },
      {
        q: "What is the Pre-Assessment Intelligence Scan?",
        a: "A Ransomware dashboard panel that scans dark web and channel threat signals for your domain before you even assess — showing a pre-assessment risk indicator, recent credential exposures, high-severity signal counts, and incidents raised, with a Rescan button."
      }
    ]
  },
  {
    category: "AI Assistant & Other Tools",
    questions: [
      {
        q: "What is the Ax AI assistant?",
        a: "Click the Ax logo in the navigation bar (available to anyone with at least Viewer access) to ask questions about your organization's risk data in plain language. It can also produce analytics reports: compliance score, top risks, remediation summary, and control health. It is strictly read-only, works on the currently selected organization only, and conversations are not saved."
      },
      {
        q: "What is Brand Protection?",
        a: "A dashboard (Risk Manager and above) that detects lookalike domains and newly registered certificates impersonating your brand terms, on a configurable scan schedule. You can request takedowns to registrars, hosting providers, Google Safe Browsing, and Microsoft SmartScreen, with response deadlines and automatic escalation of overdue requests."
      },
      {
        q: "What is Workforce Intelligence?",
        a: "A dashboard (Risk Manager and above) that forecasts the security workload your open findings represent — in full-time-equivalent staff across ten skill areas — over a 30, 90, or 180-day window. Enter your team's capacity for gap analysis, and use What-If scenarios (added staff, automation, MSSP hours) to compare options before committing."
      }
    ]
  },
  {
    category: "My Tasks",
    questions: [
      {
        q: "What is My Tasks?",
        a: "A personalized work queue showing everything assigned to you: assessments to complete, risks to manage, and risk acceptances waiting on your approval. Risk Managers also see items that still need an owner, with an Assign button. Completed items move to the Completed section, badged Accepted or Remediated."
      },
      {
        q: "How are tasks assigned to me?",
        a: "By Risk Managers, Admins, or other users with assignment rights. When an assessment is assigned to you, a risk is assigned to you, or you are named as an approver on a risk acceptance, the item automatically appears in your My Tasks list."
      },
      {
        q: "What is the Approvals section in My Tasks?",
        a: "Risk acceptances waiting on you as the named primary or secondary approver. Click Review to see the risk and the requester's justification, then approve or reject."
      }
    ]
  },
  {
    category: "Contact & Support",
    questions: [
      {
        q: "How do I contact support?",
        a: "You can reach our support team by visiting the Contact Us page. Click 'Contact Us' in the navigation menu or go to /contact. Fill in a subject and description of your question or issue and submit the form. You can also email us directly at {supportEmail}. Our team will receive your message and get back to you."
      },
      {
        q: "How do I send an email to the support team?",
        a: "Use the Contact Us form available from the navigation menu. Enter a subject line and describe your issue in the description field, then click Submit. This sends your message directly to our support team and you'll receive a response. If you prefer, you can email the team directly at {supportEmail}."
      },
      {
        q: "How do I report a bug or request a feature?",
        a: "To report a bug or request a new feature, visit the Contact Us page from the navigation menu. Provide a clear subject (e.g., 'Bug Report' or 'Feature Request') and include as much detail as possible in the description. Our team will review your submission and follow up."
      },
      {
        q: "How do I get help with Axaris?",
        a: "There are several ways to get help: browse the FAQ page for answers to common questions, use the help chat to search these answers quickly, ask the Ax AI assistant about your own risk data, or visit the Contact Us page to send a message directly to our support team. You can also email us at {supportEmail}."
      }
    ]
  }
];

// Answers that mention the support address use this placeholder, always in a sentence of its own,
// so an installation without a support address can drop that sentence and keep a useful answer.
export const SUPPORT_EMAIL_PLACEHOLDER = "{supportEmail}";

// Sentences end at ". ", "? " or "! " (the terminator stays with its sentence) or the end of the
// text. Abbreviations like "e.g.," and addresses like "a@b.c" don't split, as no space follows.
const splitSentences = (text) => {
  const sentences = [];
  let start = 0;
  for (let i = 0; i < text.length - 1; i++) {
    if (".?!".includes(text[i]) && text[i + 1] === " ") {
      sentences.push(text.slice(start, i + 1));
      start = i + 2;
    }
  }
  sentences.push(text.slice(start));
  return sentences.filter(sentence => sentence.length > 0);
};

// With an address, substitute it for the placeholder; with none (null while the instance settings
// load, on error, or when the installation has no support address), drop each sentence that
// contains the placeholder.
export const resolveFaqAnswer = (text, supportEmail) => {
  if (typeof text !== "string" || !text.includes(SUPPORT_EMAIL_PLACEHOLDER)) return text;
  if (supportEmail) return text.split(SUPPORT_EMAIL_PLACEHOLDER).join(supportEmail);
  return splitSentences(text)
    .filter(sentence => !sentence.includes(SUPPORT_EMAIL_PLACEHOLDER))
    .join(" ");
};

export const getAllFaqItems = (supportEmail = null) => {
  return faqData.flatMap(section =>
    section.questions.map(item => ({
      category: section.category,
      q: item.q,
      a: resolveFaqAnswer(item.a, supportEmail)
    }))
  );
};

export default faqData;
